import { type FocusEvent, lazy, Suspense, useState } from 'react'
import { Link, NavLink, useMatch, useSearchParams } from 'react-router'

import { Logo } from '@/components/brand/Logo'
import { Icon } from '@/components/icons/Icon'
import { cartCount } from '@/features/cart/selectors'
import { BrowseStrip } from '@/features/catalog/BrowseStrip'
import { useCartStore } from '@/features/cart/store'
import { useTowns } from '@/features/catalog/api'
import { promptInstall, useInstallMode } from '@/features/pwa/install'
import { useSuggest } from '@/features/search/api'
import { suggestionRows } from '@/features/search/suggestions'
import { SuggestList } from '@/features/search/SuggestList'
import { useTownStore } from '@/features/town/store'
import { useDebounced } from '@/hooks/useDebounced'
import { env } from '@/lib/env'
import { addRecentSearch } from '@/lib/recentSearches'
import { paths } from '@/lib/routes'
import { isSearchable } from '@/lib/search'
import type { Suggestion } from '@/types/api'

import styles from './Header.module.css'

// Only needed after a tap: keep it out of the first load.
const TownSheet = lazy(() =>
  import('@/features/town/TownSheet').then((m) => ({ default: m.TownSheet })),
)
const InstallSheet = lazy(() =>
  import('@/features/prompts/InstallSheet').then((m) => ({ default: m.InstallSheet })),
)
const MobileMenu = lazy(() => import('./MobileMenu').then((m) => ({ default: m.MobileMenu })))

/**
 * The live search field on /search; bound to `?q=` (shareable, BR-8). While typing, suggestions
 * open under it (D-91): the down arrow moves into them, Escape closes them.
 */
function SearchField() {
  const [params, setParams] = useSearchParams()
  const town = useTownStore((s) => s.town)
  const value = params.get('q') ?? ''
  const [focused, setFocused] = useState(false)
  // Closed for the words it arrived with (a shared link, a chip): it opens once the shopper types.
  const [closedFor, setClosedFor] = useState<string | null>(() => params.get('q'))
  const typed = useDebounced(value, 200)
  const { data } = useSuggest(typed, town)
  const items = (isSearchable(value) && data?.suggestions) || []
  const open = focused && items.length > 0 && closedFor !== value
  const input = () => document.getElementById('header-q') as HTMLInputElement | null
  // Open while the focus is in the field or the list (the arrow keys move into the list).
  const leave = (e: FocusEvent<HTMLElement>) => {
    const to = e.relatedTarget as Node | null
    if (!to || !e.currentTarget.closest('form')?.contains(to)) setFocused(false)
  }

  const setQ = (q: string) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p)
        if (q) next.set('q', q)
        else next.delete('q')
        return next
      },
      { replace: true },
    )
  const pick = (item: Suggestion) => {
    addRecentSearch(item.text)
    setClosedFor(item.kind === 'query' ? item.text : value)
    if (item.kind === 'query') setQ(item.text)
    input()?.blur()
    setFocused(false)
  }

  return (
    <form
      className={styles.search}
      onSubmit={(e) => {
        e.preventDefault()
        if (isSearchable(value)) addRecentSearch(value)
        setClosedFor(value)
        input()?.blur()
      }}
    >
      <Icon name="search" />
      <label htmlFor="header-q" className="visually-hidden">
        Search products or shops
      </label>
      <input
        id="header-q"
        name="q"
        type="search"
        enterKeyHint="search"
        placeholder="Search products or shops"
        autoComplete="off"
        aria-controls={open ? 'search-suggest' : undefined}
        // The search page exists to type in: focus it (PRD §4).
        // oxlint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={leave}
        onKeyDown={(e) => {
          if (!open) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            suggestionRows()[0]?.focus()
          } else if (e.key === 'Escape') {
            setClosedFor(value)
          }
        }}
        onChange={(e) => {
          setClosedFor(null)
          setQ(e.target.value)
        }}
      />
      {open && (
        <SuggestList
          items={items}
          onPick={pick}
          onEscape={() => {
            setClosedFor(value)
            input()?.focus()
          }}
          onBlur={leave}
        />
      )}
    </form>
  )
}

export function Header({ showSearch }: { showSearch: boolean }) {
  const onSearch = useMatch('/search') != null
  const count = useCartStore((s) => cartCount(s.lines))
  const town = useTownStore((s) => s.town)
  const { data: towns } = useTowns()
  const [townOpen, setTownOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [installOpen, setInstallOpen] = useState(false)
  const installMode = useInstallMode()
  const townName = towns?.find((t) => t.slug === town)?.name ?? 'All towns'

  return (
    <header className={styles.head}>
      {env.demo && (
        <p className={styles.demo}>Demo · sample shops and products, nothing here is real</p>
      )}
      <div className={`wrap ${styles.row}`}>
        <Link to={paths.home()} className={styles.logo} aria-label="townshop.in home">
          <Logo />
        </Link>
        <nav className={styles.tabs} aria-label="Main">
          <NavLink to={paths.home()} end>
            Home
          </NavLink>
          <NavLink to={paths.shops()}>Shops</NavLink>
          <NavLink to={paths.search()}>Search</NavLink>
        </nav>
        <span className={styles.spacer} />
        <button
          className={styles.town}
          onClick={() => setTownOpen(true)}
          aria-label={`Town: ${townName}. Change town`}
        >
          <Icon name="pin" size={16} />
          <span>{townName}</span>
          <Icon name="down" size={14} />
        </button>
        <a
          className={styles.shopLogin}
          href={`${env.ownerAppUrl.replace(/\/$/, '')}/login`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="store" size={18} />
          Shop login
        </a>
        <Link
          to={paths.cart()}
          className={styles.cart}
          aria-label={`Cart, ${count} ${count === 1 ? 'item' : 'items'}`}
        >
          <Icon name="cart" size={24} />
          {count > 0 && <span className={`${styles.badge} num`}>{count}</span>}
        </Link>
        <button
          type="button"
          className={styles.menuBtn}
          aria-label="Menu"
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <Icon name="menu" size={24} />
        </button>
      </div>
      {showSearch && (
        <div className={`wrap ${styles.searchRow}`}>
          {onSearch ? (
            <SearchField />
          ) : (
            <Link to={paths.search()} className={styles.search}>
              <Icon name="search" />
              <span>Search sarees, rice, earbuds, shops…</span>
            </Link>
          )}
        </div>
      )}
      {/* Laptops: collections and categories in one slim row (D-94); phones get it on Home. */}
      {showSearch && (
        <div className="wrap">
          <BrowseStrip variant="bar" />
        </div>
      )}
      {menuOpen && (
        <Suspense fallback={null}>
          <MobileMenu
            townName={townName}
            onChooseTown={() => {
              setMenuOpen(false)
              setTownOpen(true)
            }}
            onInstall={
              installMode
                ? () => {
                    setMenuOpen(false)
                    if (installMode === 'prompt') void promptInstall()
                    else setInstallOpen(true)
                  }
                : undefined
            }
            onClose={() => setMenuOpen(false)}
          />
        </Suspense>
      )}
      {installOpen && installMode && (
        <Suspense fallback={null}>
          <InstallSheet mode={installMode} onClose={() => setInstallOpen(false)} />
        </Suspense>
      )}
      {townOpen && (
        <Suspense fallback={null}>
          <TownSheet onClose={() => setTownOpen(false)} />
        </Suspense>
      )}
    </header>
  )
}
